import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PagePaths } from '@revelio/core';

import {
  DATA_BUILDER_STEPS,
  DataBuilderPaths,
} from './steps/data-builder-steps';

const getPathSuffix = (pathname: string, basePath: string) => {
  if (pathname === basePath) {
    return '';
  }

  const stepPrefix = `${basePath}/`;
  if (pathname.startsWith(stepPrefix)) {
    return pathname.slice(stepPrefix.length);
  }

  return '';
};

const getCurrentStepIndex = (pathname: string, basePath: string) => {
  const pathSuffix = getPathSuffix(pathname, basePath);
  if (pathSuffix.length === 0) {
    return -1;
  }

  return DATA_BUILDER_STEPS.findIndex((step) => step.path === pathSuffix);
};

export const useDataBuilderNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = `/${PagePaths.DATA_BUILDER}`;
  const pathSuffix = getPathSuffix(location.pathname, basePath);
  const isChooseStep = pathSuffix === DataBuilderPaths.CHOOSE;
  const isSavedDatasetsStep = pathSuffix === DataBuilderPaths.SAVED_DATASETS;

  useEffect(() => {
    if (
      location.pathname === basePath ||
      location.pathname.endsWith(PagePaths.DATA_BUILDER)
    ) {
      navigate(`${basePath}/${DataBuilderPaths.CHOOSE}`, { replace: true });
    }
  }, [basePath, location.pathname, navigate]);

  const currentStepIndex = getCurrentStepIndex(location.pathname, basePath);
  const safeStepIndex = currentStepIndex >= 0 ? currentStepIndex : 0;

  const previousStep = DATA_BUILDER_STEPS[safeStepIndex - 1];
  const nextStep = DATA_BUILDER_STEPS[safeStepIndex + 1];
  const hasNextStep = nextStep !== undefined;

  const previousPath = (() => {
    if (isSavedDatasetsStep) {
      return `${basePath}/${DataBuilderPaths.CHOOSE}`;
    }

    if (previousStep === undefined) {
      return undefined;
    }

    return `${basePath}/${previousStep.path}`;
  })();

  const goToNextStep = () => {
    if (nextStep !== undefined) {
      navigate(`${basePath}/${nextStep.path}`);
    }
  };

  const goToChooseStep = () => {
    navigate(`${basePath}/${DataBuilderPaths.CHOOSE}`);
  };

  const goToSavedDatasetsStep = () => {
    navigate(`${basePath}/${DataBuilderPaths.SAVED_DATASETS}`);
  };

  const goToDatasetStep = () => {
    navigate(`${basePath}/${DataBuilderPaths.DATASET}`);
  };

  return {
    safeStepIndex,
    isChooseStep,
    isSavedDatasetsStep,
    previousPath,
    goToNextStep,
    hasNextStep,
    goToChooseStep,
    goToSavedDatasetsStep,
    goToDatasetStep,
  };
};
